import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface S3BackupSettings {
  id: string;
  s3_bucket: string;
  s3_region: string;
  s3_access_key_id: string | null;
  s3_secret_access_key: string | null;
  s3_endpoint: string | null;
  storage_class: string;
  encryption_enabled: boolean;
  compression_enabled: boolean;
  compression_algorithm: string;
  default_retention_days: number;
  max_concurrent_backups: number;
  enabled: boolean;
}

interface S3Backup {
  id: string;
  vps_id: string;
  vps_name: string;
  vps_hostname: string;
  proxmox_vmid: number;
  backup_type: string;
  backup_mode: string;
  name: string;
  description: string | null;
  s3_bucket: string;
  s3_key: string;
  s3_region: string;
  file_name: string;
  file_size_bytes: number | null;
  file_size_gb: number | null;
  compression: string;
  status: string;
  progress_percent: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  retention_days: number;
  expires_at: string | null;
  is_protected: boolean;
  created_at: string;
}

interface S3BackupSchedule {
  id: string;
  proxmox_host_id: string | null;
  proxmox_host_name: string | null;
  name: string;
  description: string | null;
  backup_all_vms: boolean;
  backup_type: string;
  backup_mode: string;
  compression: string;
  frequency: string;
  day_of_week: number | null;
  day_of_month: number | null;
  time_utc: string;
  retention_count: number;
  retention_days: number;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
}

interface BackupStats {
  total_backups: number;
  completed_backups: number;
  failed_backups: number;
  pending_backups: number;
  in_progress_backups: number;
  total_size_gb: number;
  total_vps_backed_up: number;
  oldest_backup: string | null;
  newest_backup: string | null;
  s3_enabled: boolean;
  scheduler_running: boolean;
  scheduler_processing: boolean;
  active_schedules: number;
}

interface BackupJob {
  id: string;
  schedule_id: string | null;
  schedule_name: string | null;
  job_type: string;
  status: string;
  total_vms: number;
  completed_vms: number;
  failed_vms: number;
  success_count: number;
  failure_count: number;
  total_size_bytes: number;
  started_at: string | null;
  completed_at: string | null;
  error_messages: string[];
}

type TabType = 'dashboard' | 'backups' | 'schedules' | 'settings';

const S3BackupManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [backups, setBackups] = useState<S3Backup[]>([]);
  const [schedules, setSchedules] = useState<S3BackupSchedule[]>([]);
  const [jobs, setJobs] = useState<BackupJob[]>([]);
  const [settings, setSettings] = useState<S3BackupSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedBackup, setSelectedBackup] = useState<S3Backup | null>(null);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    s3_bucket: '',
    s3_region: 'us-east-1',
    s3_access_key_id: '',
    s3_secret_access_key: '',
    s3_endpoint: '',
    storage_class: 'STANDARD',
    compression_algorithm: 'zstd',
    default_retention_days: 30,
    max_concurrent_backups: 2,
    enabled: false
  });

  // Schedule form state
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    description: '',
    backup_all_vms: true,
    backup_mode: 'snapshot',
    compression: 'zstd',
    frequency: 'weekly',
    day_of_week: 0,
    time_utc: '02:00',
    retention_count: 4,
    retention_days: 30,
    enabled: true
  });

  // Backup form state
  const [showBackupForm, setShowBackupForm] = useState(false);
  const [backupVpsId, setBackupVpsId] = useState('');
  const [backupName, setBackupName] = useState('');
  const [backupDescription, setBackupDescription] = useState('');
  const [vpsList, setVpsList] = useState<any[]>([]);

  // Filter state
  const [backupFilter, setBackupFilter] = useState<'all' | 'completed' | 'failed' | 'in_progress'>('all');

  // Restore modal state
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreBackup, setRestoreBackup] = useState<S3Backup | null>(null);
  const [restoreOptions, setRestoreOptions] = useState({
    targetVmid: '',
    targetStorage: 'local-zfs',
    startAfterRestore: true,
    restoreMode: 'same' as 'same' | 'new' | 'cross-host',
    // Cross-host restore options
    targetHostId: '',
    allocateNewIp: true,
    specificIpAddress: '',
    createNewVpsRecord: true,
    newVpsName: ''
  });
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreStatus, setRestoreStatus] = useState('');

  // Cross-host restore data
  const [proxmoxHosts, setProxmoxHosts] = useState<any[]>([]);
  const [availableIps, setAvailableIps] = useState<any[]>([]);
  const [loadingHosts, setLoadingHosts] = useState(false);
  const [loadingIps, setLoadingIps] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/s3-backups/stats`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchBackups = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/s3-backups/backups`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch backups:', err);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/s3-backups/schedules`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setSchedules(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch schedules:', err);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/s3-backups/jobs?limit=20`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch jobs:', err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/s3-backups/settings`, {
        headers: getAuthHeaders()
      });
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        setSettingsForm({
          s3_bucket: response.data.data.s3_bucket || '',
          s3_region: response.data.data.s3_region || 'us-east-1',
          s3_access_key_id: '',
          s3_secret_access_key: '',
          s3_endpoint: response.data.data.s3_endpoint || '',
          storage_class: response.data.data.storage_class || 'STANDARD',
          compression_algorithm: response.data.data.compression_algorithm || 'zstd',
          default_retention_days: response.data.data.default_retention_days || 30,
          max_concurrent_backups: response.data.data.max_concurrent_backups || 2,
          enabled: response.data.data.enabled || false
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  const fetchVPSList = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vps/instances`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setVpsList(response.data.data.filter((v: any) => v.proxmox_vmid));
      }
    } catch (err: any) {
      console.error('Failed to fetch VPS list:', err);
    }
  }, []);

  // Fetch Proxmox hosts for cross-host restore
  const fetchProxmoxHosts = useCallback(async () => {
    setLoadingHosts(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setProxmoxHosts(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch Proxmox hosts:', err);
    } finally {
      setLoadingHosts(false);
    }
  }, []);

  // Fetch available IPs for a specific host
  const fetchAvailableIps = useCallback(async (hostId: string) => {
    if (!hostId) {
      setAvailableIps([]);
      return;
    }
    setLoadingIps(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/ipv4-pool/available?hostId=${hostId}`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setAvailableIps(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch available IPs:', err);
      setAvailableIps([]);
    } finally {
      setLoadingIps(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchBackups(),
        fetchSchedules(),
        fetchJobs(),
        fetchSettings(),
        fetchVPSList()
      ]);
      setLoading(false);
    };
    loadData();

    const interval = setInterval(() => {
      fetchStats();
      fetchBackups();
      fetchJobs();
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchStats, fetchBackups, fetchSchedules, fetchJobs, fetchSettings, fetchVPSList]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 8000);
  };

  const handleSaveSettings = async () => {
    try {
      const dataToSend: any = { ...settingsForm };
      if (!dataToSend.s3_access_key_id) delete dataToSend.s3_access_key_id;
      if (!dataToSend.s3_secret_access_key) delete dataToSend.s3_secret_access_key;
      if (!dataToSend.s3_endpoint) delete dataToSend.s3_endpoint;

      const response = await axios.put(`${API_URL}/api/admin/s3-backups/settings`, dataToSend, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess('S3 backup settings saved successfully');
        fetchSettings();
        fetchStats();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleTestConnection = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/s3-backups/settings/test`, {}, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess(`S3 connection successful! Bucket: ${response.data.details?.bucket}`);
      } else {
        showError(response.data.message || 'Connection test failed');
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Connection test failed');
    }
  };

  const handleCreateBackup = async () => {
    if (!backupVpsId) {
      showError('Please select a VPS to backup');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/s3-backups/vps/${backupVpsId}`, {
        name: backupName || undefined,
        description: backupDescription || undefined
      }, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess('Backup initiated successfully. Check the Backups tab for progress.');
        setShowBackupForm(false);
        setBackupVpsId('');
        setBackupName('');
        setBackupDescription('');
        fetchBackups();
        fetchStats();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to create backup');
    }
  };

  const handleDeleteBackup = async (backupId: string, backupName: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete backup "${backupName}"?\n\nThis will remove the backup from S3 storage and cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_URL}/api/admin/s3-backups/backups/${backupId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess('Backup deleted successfully');
        fetchBackups();
        fetchStats();
        setSelectedBackup(null);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete backup');
    }
  };

  // Open restore modal
  const openRestoreModal = (backup: S3Backup) => {
    setRestoreBackup(backup);
    setRestoreOptions({
      targetVmid: '',
      targetStorage: 'local-zfs',
      startAfterRestore: true,
      restoreMode: 'same',
      targetHostId: '',
      allocateNewIp: true,
      specificIpAddress: '',
      createNewVpsRecord: true,
      newVpsName: backup.vps_name ? `${backup.vps_name}-restored` : ''
    });
    setRestoreInProgress(false);
    setRestoreProgress(0);
    setRestoreStatus('');
    setAvailableIps([]);
    // Fetch hosts when modal opens
    fetchProxmoxHosts();
    setShowRestoreModal(true);
  };

  // Execute restore
  const executeRestore = async () => {
    if (!restoreBackup) return;

    // Validation based on restore mode
    if (restoreOptions.restoreMode === 'new') {
      const targetVmid = parseInt(restoreOptions.targetVmid);
      if (!restoreOptions.targetVmid || isNaN(targetVmid)) {
        showError('Please enter a valid VMID for the new VM');
        return;
      }
    }

    if (restoreOptions.restoreMode === 'cross-host') {
      if (!restoreOptions.targetHostId) {
        showError('Please select a target Proxmox host');
        return;
      }
      if (restoreOptions.allocateNewIp && !restoreOptions.specificIpAddress && availableIps.length === 0) {
        showError('No available IP addresses on the selected host');
        return;
      }
      if (restoreOptions.createNewVpsRecord && !restoreOptions.newVpsName) {
        showError('Please enter a name for the new VPS record');
        return;
      }
    }

    setRestoreInProgress(true);
    setRestoreProgress(5);
    setRestoreStatus('Initiating restore...');

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setRestoreProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 8;
        });
      }, 3000);

      setRestoreStatus(
        restoreOptions.restoreMode === 'cross-host'
          ? 'Downloading backup from S3 and preparing cross-host restore...'
          : 'Downloading backup from S3...'
      );
      setRestoreProgress(10);

      // Build request body based on restore mode
      const requestBody: any = {
        targetStorage: restoreOptions.targetStorage,
        startAfterRestore: restoreOptions.startAfterRestore
      };

      if (restoreOptions.restoreMode === 'new') {
        requestBody.targetVmid = parseInt(restoreOptions.targetVmid);
      } else if (restoreOptions.restoreMode === 'cross-host') {
        requestBody.targetHostId = parseInt(restoreOptions.targetHostId);
        requestBody.allocateNewIp = restoreOptions.allocateNewIp;
        if (restoreOptions.specificIpAddress) {
          requestBody.specificIpAddress = restoreOptions.specificIpAddress;
        }
        requestBody.createNewVpsRecord = restoreOptions.createNewVpsRecord;
        if (restoreOptions.newVpsName) {
          requestBody.newVpsName = restoreOptions.newVpsName;
        }
      }

      const response = await axios.post(`${API_URL}/api/admin/s3-backups/backups/${restoreBackup.id}/restore`, requestBody, {
        headers: getAuthHeaders()
      });

      clearInterval(progressInterval);

      if (response.data.success) {
        setRestoreProgress(100);

        // Build success message based on response data
        let successMsg = response.data.message || 'VM restored successfully from backup';
        if (response.data.data?.newIpAddress) {
          successMsg += ` New IP: ${response.data.data.newIpAddress}`;
        }
        if (response.data.data?.newVmid) {
          successMsg += ` VMID: ${response.data.data.newVmid}`;
        }

        setRestoreStatus('Restore completed successfully!');
        showSuccess(successMsg);

        // Close modal after short delay
        setTimeout(() => {
          setShowRestoreModal(false);
          setRestoreBackup(null);
          fetchBackups();
          fetchStats();
          fetchVPSList();
        }, 2500);
      }
    } catch (err: any) {
      setRestoreStatus('Restore failed');
      showError(err.response?.data?.error || 'Failed to restore backup');
      setRestoreInProgress(false);
    }
  };

  // Legacy handler for backward compatibility
  const handleRestoreBackup = async (backupId: string, _vpsName: string) => {
    const backup = backups.find(b => b.id === backupId);
    if (backup) {
      openRestoreModal(backup);
    }
  };

  const handleCreateSchedule = async () => {
    if (!scheduleForm.name) {
      showError('Please enter a schedule name');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/s3-backups/schedules`, {
        ...scheduleForm,
        time_utc: scheduleForm.time_utc + ':00'
      }, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess('Backup schedule created successfully');
        setShowScheduleForm(false);
        setScheduleForm({
          name: '',
          description: '',
          backup_all_vms: true,
          backup_mode: 'snapshot',
          compression: 'zstd',
          frequency: 'weekly',
          day_of_week: 0,
          time_utc: '02:00',
          retention_count: 4,
          retention_days: 30,
          enabled: true
        });
        fetchSchedules();
        fetchStats();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string, scheduleName: string) => {
    if (!window.confirm(`Delete schedule "${scheduleName}"?\n\nExisting backups created by this schedule will NOT be deleted.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_URL}/api/admin/s3-backups/schedules/${scheduleId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess('Schedule deleted successfully');
        fetchSchedules();
        fetchStats();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete schedule');
    }
  };

  const handleToggleSchedule = async (scheduleId: string, enabled: boolean) => {
    try {
      await axios.put(`${API_URL}/api/admin/s3-backups/schedules/${scheduleId}`, {
        enabled: !enabled
      }, {
        headers: getAuthHeaders()
      });
      fetchSchedules();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to update schedule');
    }
  };

  const handleRunScheduleNow = async (scheduleId: string, scheduleName: string) => {
    if (!window.confirm(`Run schedule "${scheduleName}" now?\n\nThis will immediately start backing up all VMs in this schedule.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/s3-backups/schedules/${scheduleId}/run`, {}, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess(`Schedule "${scheduleName}" started. ${response.data.message || ''}`);
        fetchBackups();
        fetchJobs();
        fetchStats();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to run schedule');
    }
  };

  const handleRunAllBackupsNow = async () => {
    if (!window.confirm('Backup ALL running VMs now?\n\nThis will create a full backup of every running VM and upload to S3.')) {
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/s3-backups/run-all`, {}, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        showSuccess(response.data.message || 'Full backup initiated for all VMs');
        fetchBackups();
        fetchJobs();
        fetchStats();
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to run backups');
    }
  };

  // Utility functions
  const formatFileSize = (bytes: number | null): string => {
    if (!bytes || bytes === 0) return '—';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const formatRelativeTime = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  const formatDuration = (startStr: string | null, endStr: string | null): string => {
    if (!startStr) return '—';
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : new Date();
    const diff = end.getTime() - start.getTime();

    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getStatusConfig = (status: string) => {
    const configs: { [key: string]: { bg: string; text: string; icon: string; label: string } } = {
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: '✓', label: 'Completed' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: '✕', label: 'Failed' },
      creating: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '◐', label: 'Creating' },
      uploading: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '↑', label: 'Uploading' },
      pending: { bg: 'bg-gray-100', text: 'text-gray-600', icon: '○', label: 'Pending' },
      restoring: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '↓', label: 'Restoring' },
      running: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '◉', label: 'Running' },
      partial: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '◑', label: 'Partial' },
      deleting: { bg: 'bg-red-100', text: 'text-red-800', icon: '◌', label: 'Deleting' }
    };
    return configs[status] || { bg: 'bg-gray-100', text: 'text-gray-600', icon: '?', label: status };
  };

  const getDayName = (day: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || 'Unknown';
  };

  const getStorageClassInfo = (storageClass: string) => {
    const info: { [key: string]: { label: string; description: string; costTier: string } } = {
      'STANDARD': { label: 'Standard', description: 'High availability, frequent access', costTier: '$$$' },
      'STANDARD_IA': { label: 'Standard-IA', description: 'Infrequent access, lower cost', costTier: '$$' },
      'ONEZONE_IA': { label: 'One Zone-IA', description: 'Single AZ, lowest IA cost', costTier: '$' },
      'GLACIER': { label: 'Glacier', description: 'Archive, retrieval in minutes', costTier: '$' },
      'DEEP_ARCHIVE': { label: 'Deep Archive', description: 'Long-term archive, 12h retrieval', costTier: '¢' }
    };
    return info[storageClass] || { label: storageClass, description: '', costTier: '?' };
  };

  const getFilteredBackups = () => {
    if (backupFilter === 'all') return backups;
    if (backupFilter === 'completed') return backups.filter(b => b.status === 'completed');
    if (backupFilter === 'failed') return backups.filter(b => b.status === 'failed');
    if (backupFilter === 'in_progress') return backups.filter(b => ['creating', 'uploading', 'pending', 'restoring'].includes(b.status));
    return backups;
  };

  const estimateMonthlyCost = (sizeGb: number, storageClass: string): string => {
    const rates: { [key: string]: number } = {
      'STANDARD': 0.023,
      'STANDARD_IA': 0.0125,
      'ONEZONE_IA': 0.01,
      'GLACIER': 0.004,
      'DEEP_ARCHIVE': 0.00099
    };
    const rate = rates[storageClass] || 0.023;
    const cost = sizeGb * rate;
    return cost < 0.01 ? '< $0.01' : `$${cost.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading S3 Backup Management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Disaster Recovery & Cloud Backup</h1>
            <p className="text-sm text-gray-500 mt-1">Enterprise S3-based VM backup solution with automated scheduling</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Connection Status */}
            <div className={`flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
              stats?.s3_enabled
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              <span className={`w-2 h-2 rounded-full mr-2 ${stats?.s3_enabled ? 'bg-green-500' : 'bg-red-500'}`}></span>
              {stats?.s3_enabled ? 'S3 Connected' : 'S3 Not Configured'}
            </div>
            {/* Scheduler Status */}
            <div className={`flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
              stats?.scheduler_running
                ? stats?.scheduler_processing
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            }`}>
              <span className={`w-2 h-2 rounded-full mr-2 ${
                stats?.scheduler_running
                  ? stats?.scheduler_processing ? 'bg-blue-500 animate-pulse' : 'bg-green-500'
                  : 'bg-gray-400'
              }`}></span>
              {stats?.scheduler_running
                ? stats?.scheduler_processing ? 'Scheduler Active' : 'Scheduler Running'
                : 'Scheduler Stopped'}
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-red-500 text-xl mr-3">⚠</span>
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">✕</button>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="mx-6 mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-green-500 text-xl mr-3">✓</span>
            <p className="text-green-800">{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-500 hover:text-green-700">✕</button>
          </div>
        </div>
      )}

      {/* Configuration Warning */}
      {!stats?.s3_enabled && (
        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <span className="text-amber-500 text-2xl mr-3">⚙</span>
            <div className="flex-1">
              <h3 className="text-amber-800 font-semibold">S3 Configuration Required</h3>
              <p className="text-amber-700 text-sm mt-1">
                Cloud backups are currently disabled. Configure your S3 credentials in the Settings tab to enable automated disaster recovery backups.
              </p>
              <button
                onClick={() => setActiveTab('settings')}
                className="mt-2 text-sm font-medium text-amber-800 hover:text-amber-900 underline"
              >
                Configure S3 Settings →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <nav className="flex space-x-8">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'backups', label: 'Backups', icon: '💾', count: backups.length },
            { id: 'schedules', label: 'Schedules', icon: '📅', count: schedules.length },
            { id: 'settings', label: 'Settings', icon: '⚙' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Backups */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Backups</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.total_backups || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">💾</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-sm">
                  <span className="text-green-600 font-medium">{stats?.completed_backups || 0} completed</span>
                  <span className="mx-2 text-gray-300">|</span>
                  <span className="text-red-600 font-medium">{stats?.failed_backups || 0} failed</span>
                </div>
              </div>

              {/* Storage Used */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Storage Used</p>
                    <p className="text-3xl font-bold text-blue-600 mt-1">{parseFloat(String(stats?.total_size_gb || 0)).toFixed(2)} GB</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">☁️</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-500">
                  Est. cost: {estimateMonthlyCost(parseFloat(String(stats?.total_size_gb || 0)), settings?.storage_class || 'STANDARD')}/mo
                </div>
              </div>

              {/* VMs Protected */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">VMs Protected</p>
                    <p className="text-3xl font-bold text-green-600 mt-1">{stats?.total_vps_backed_up || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🛡️</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-500">
                  {vpsList.length} total VMs in system
                </div>
              </div>

              {/* Active Schedules */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Active Schedules</p>
                    <p className="text-3xl font-bold text-purple-600 mt-1">{schedules.filter(s => s.enabled).length}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📅</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-500">
                  {schedules.length} total schedules
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowBackupForm(true)}
                  disabled={!stats?.s3_enabled}
                  className="inline-flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <span className="mr-2">💾</span> Create Manual Backup
                </button>
                <button
                  onClick={handleRunAllBackupsNow}
                  disabled={!stats?.s3_enabled}
                  className="inline-flex items-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <span className="mr-2">🚀</span> Backup All VMs Now
                </button>
                <button
                  onClick={() => { setShowScheduleForm(true); setActiveTab('schedules'); }}
                  className="inline-flex items-center px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  <span className="mr-2">➕</span> Create Schedule
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className="inline-flex items-center px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  <span className="mr-2">⚙</span> Configure S3
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Jobs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Backup Jobs</h3>
                  <span className="text-sm text-gray-500">{jobs.length} jobs</span>
                </div>
                {jobs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-2 block">📋</span>
                    <p>No backup jobs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobs.slice(0, 5).map((job) => {
                      const statusConfig = getStatusConfig(job.status);
                      return (
                        <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className={`w-8 h-8 ${statusConfig.bg} ${statusConfig.text} rounded-full flex items-center justify-center text-sm font-bold`}>
                              {statusConfig.icon}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{job.schedule_name || 'Manual Backup'}</p>
                              <p className="text-xs text-gray-500">
                                {job.success_count}/{job.total_vms} VMs • {formatRelativeTime(job.started_at)}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Recent Backups */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Backups</h3>
                  <button
                    onClick={() => setActiveTab('backups')}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View All →
                  </button>
                </div>
                {backups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-2 block">💾</span>
                    <p>No backups created yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {backups.slice(0, 5).map((backup) => {
                      const statusConfig = getStatusConfig(backup.status);
                      return (
                        <div key={backup.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className={`w-8 h-8 ${statusConfig.bg} ${statusConfig.text} rounded-full flex items-center justify-center text-sm font-bold`}>
                              {statusConfig.icon}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{backup.vps_name || backup.vps_hostname}</p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(backup.file_size_bytes)} • {formatRelativeTime(backup.created_at)}
                              </p>
                            </div>
                          </div>
                          {backup.status === 'uploading' ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${backup.progress_percent}%` }}></div>
                              </div>
                              <span className="text-xs text-gray-500">{backup.progress_percent}%</span>
                            </div>
                          ) : (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
                              {statusConfig.label}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Active Schedules Overview */}
            {schedules.filter(s => s.enabled).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Backup Schedules</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {schedules.filter(s => s.enabled).map((schedule) => (
                    <div key={schedule.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{schedule.name}</h4>
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Active</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {schedule.frequency === 'weekly' ? `Every ${getDayName(schedule.day_of_week || 0)}` :
                         schedule.frequency === 'monthly' ? `Monthly on day ${schedule.day_of_month || 1}` : 'Daily'} at {schedule.time_utc} UTC
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Next: {schedule.next_run_at ? formatRelativeTime(schedule.next_run_at) : 'Not scheduled'}</span>
                        <button
                          onClick={() => handleRunScheduleNow(schedule.id, schedule.name)}
                          disabled={!stats?.s3_enabled}
                          className="text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400"
                        >
                          Run Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Backups Tab */}
        {activeTab === 'backups' && (
          <div className="space-y-4">
            {/* Filters and Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Filter:</span>
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'completed', label: 'Completed' },
                    { id: 'failed', label: 'Failed' },
                    { id: 'in_progress', label: 'In Progress' }
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setBackupFilter(filter.id as any)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        backupFilter === filter.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowBackupForm(true)}
                  disabled={!stats?.s3_enabled}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  <span className="mr-2">➕</span> Create Backup
                </button>
              </div>
            </div>

            {/* Backups List */}
            {getFilteredBackups().length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <span className="text-6xl mb-4 block">💾</span>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Backups Found</h3>
                <p className="text-gray-500 mb-4">
                  {backupFilter !== 'all'
                    ? `No ${backupFilter.replace('_', ' ')} backups found. Try a different filter.`
                    : 'Create your first backup to protect your VMs.'}
                </p>
                {backupFilter === 'all' && (
                  <button
                    onClick={() => setShowBackupForm(true)}
                    disabled={!stats?.s3_enabled}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                  >
                    Create First Backup
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">VM / Backup</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Size</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Expires</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredBackups().map((backup) => {
                      const statusConfig = getStatusConfig(backup.status);
                      return (
                        <tr key={backup.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-lg">🖥️</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{backup.vps_name || backup.vps_hostname}</p>
                                <p className="text-xs text-gray-500">VMID: {backup.proxmox_vmid} • {backup.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-900">{formatFileSize(backup.file_size_bytes)}</p>
                            <p className="text-xs text-gray-500">{backup.compression.toUpperCase()}</p>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                                <span className="mr-1">{statusConfig.icon}</span>
                                {statusConfig.label}
                              </span>
                            </div>
                            {backup.status === 'uploading' && (
                              <div className="mt-2 flex items-center space-x-2">
                                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${backup.progress_percent}%` }}></div>
                                </div>
                                <span className="text-xs text-gray-500">{backup.progress_percent}%</span>
                              </div>
                            )}
                            {backup.error_message && (
                              <p className="text-xs text-red-600 mt-1 truncate max-w-xs" title={backup.error_message}>{backup.error_message}</p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-900">{formatRelativeTime(backup.created_at)}</p>
                            <p className="text-xs text-gray-500">{formatDate(backup.created_at)}</p>
                          </td>
                          <td className="px-6 py-4">
                            {backup.expires_at ? (
                              <>
                                <p className="text-sm text-gray-900">{formatRelativeTime(backup.expires_at)}</p>
                                <p className="text-xs text-gray-500">{backup.retention_days} days</p>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">Never</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right space-x-2">
                            <button
                              onClick={() => setSelectedBackup(backup)}
                              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                            >
                              Details
                            </button>
                            {backup.status === 'completed' && (
                              <button
                                onClick={() => openRestoreModal(backup)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Restore
                              </button>
                            )}
                            {!backup.is_protected && !['creating', 'uploading', 'restoring'].includes(backup.status) && (
                              <button
                                onClick={() => handleDeleteBackup(backup.id, backup.name)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Backup Schedules</h2>
                <p className="text-sm text-gray-500">Automate your disaster recovery with scheduled backups</p>
              </div>
              <button
                onClick={() => setShowScheduleForm(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <span className="mr-2">➕</span> Create Schedule
              </button>
            </div>

            {schedules.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <span className="text-6xl mb-4 block">📅</span>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Schedules Configured</h3>
                <p className="text-gray-500 mb-4">Set up automated backup schedules to protect your infrastructure.</p>
                <button
                  onClick={() => setShowScheduleForm(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create First Schedule
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {schedules.map((schedule) => {
                  const lastRunStatus = schedule.last_run_status ? getStatusConfig(schedule.last_run_status) : null;
                  return (
                    <div key={schedule.id} className={`bg-white rounded-xl shadow-sm border ${schedule.enabled ? 'border-green-200' : 'border-gray-200'} p-6`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-semibold text-gray-900">{schedule.name}</h3>
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                              schedule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {schedule.enabled ? '● Active' : '○ Disabled'}
                            </span>
                          </div>
                          {schedule.description && (
                            <p className="text-gray-600 text-sm mt-1">{schedule.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleToggleSchedule(schedule.id, schedule.enabled)}
                            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                              schedule.enabled
                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {schedule.enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => handleRunScheduleNow(schedule.id, schedule.name)}
                            disabled={!stats?.s3_enabled}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-medium"
                          >
                            Run Now
                          </button>
                          <button
                            onClick={() => handleDeleteSchedule(schedule.id, schedule.name)}
                            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase font-medium">Frequency</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {schedule.frequency === 'weekly' ? getDayName(schedule.day_of_week || 0) :
                             schedule.frequency === 'monthly' ? `Day ${schedule.day_of_month || 1}` : 'Daily'}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase font-medium">Time (UTC)</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{schedule.time_utc}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase font-medium">Retention</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">{schedule.retention_count} backups</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase font-medium">Mode</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1 capitalize">{schedule.backup_mode}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase font-medium">Compression</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1 uppercase">{schedule.compression}</p>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-sm">
                        <div className="flex items-center space-x-4">
                          <span className="text-gray-500">
                            <strong>Last run:</strong> {schedule.last_run_at ? formatRelativeTime(schedule.last_run_at) : 'Never'}
                            {lastRunStatus && (
                              <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${lastRunStatus.bg} ${lastRunStatus.text}`}>
                                {lastRunStatus.label}
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="text-gray-500">
                          <strong>Next run:</strong> {schedule.next_run_at ? formatDate(schedule.next_run_at) : 'Not scheduled'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">S3 Storage Configuration</h2>

              <div className="space-y-8">
                {/* S3 Connection */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">☁️</span>
                    S3 Connection
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bucket Name *</label>
                      <input
                        type="text"
                        value={settingsForm.s3_bucket}
                        onChange={(e) => setSettingsForm({ ...settingsForm, s3_bucket: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="my-backup-bucket"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Region *</label>
                      <select
                        value={settingsForm.s3_region}
                        onChange={(e) => setSettingsForm({ ...settingsForm, s3_region: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                        <option value="us-east-2">US East (Ohio) - us-east-2</option>
                        <option value="us-west-1">US West (N. California) - us-west-1</option>
                        <option value="us-west-2">US West (Oregon) - us-west-2</option>
                        <option value="eu-west-1">EU (Ireland) - eu-west-1</option>
                        <option value="eu-west-2">EU (London) - eu-west-2</option>
                        <option value="eu-central-1">EU (Frankfurt) - eu-central-1</option>
                        <option value="ap-south-1">Asia Pacific (Mumbai) - ap-south-1</option>
                        <option value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</option>
                        <option value="ap-northeast-1">Asia Pacific (Tokyo) - ap-northeast-1</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Custom Endpoint (Optional)</label>
                      <input
                        type="text"
                        value={settingsForm.s3_endpoint}
                        onChange={(e) => setSettingsForm({ ...settingsForm, s3_endpoint: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://s3.wasabisys.com (for S3-compatible services)"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for AWS S3. Use custom endpoint for Wasabi, MinIO, Backblaze B2, etc.</p>
                    </div>
                  </div>
                </div>

                {/* Credentials */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">🔑</span>
                    AWS Credentials
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Access Key ID</label>
                      <input
                        type="text"
                        value={settingsForm.s3_access_key_id}
                        onChange={(e) => setSettingsForm({ ...settingsForm, s3_access_key_id: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                        placeholder={settings?.s3_access_key_id ? '••••••••••••••••' : 'AKIAIOSFODNN7EXAMPLE'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secret Access Key</label>
                      <input
                        type="password"
                        value={settingsForm.s3_secret_access_key}
                        onChange={(e) => setSettingsForm({ ...settingsForm, s3_secret_access_key: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                        placeholder={settings?.s3_secret_access_key ? '••••••••••••••••' : 'Enter secret key'}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Leave credentials empty if using IAM roles or environment variables. We recommend using IAM roles for production.
                  </p>
                </div>

                {/* Storage Options */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">📦</span>
                    Storage Options
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Storage Class</label>
                      <select
                        value={settingsForm.storage_class}
                        onChange={(e) => setSettingsForm({ ...settingsForm, storage_class: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="STANDARD">Standard - Frequent access ($0.023/GB)</option>
                        <option value="STANDARD_IA">Standard-IA - Infrequent access ($0.0125/GB)</option>
                        <option value="ONEZONE_IA">One Zone-IA - Single AZ ($0.01/GB)</option>
                        <option value="GLACIER">Glacier - Archive, minutes retrieval ($0.004/GB)</option>
                        <option value="DEEP_ARCHIVE">Deep Archive - Long-term ($0.00099/GB)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Compression Algorithm</label>
                      <select
                        value={settingsForm.compression_algorithm}
                        onChange={(e) => setSettingsForm({ ...settingsForm, compression_algorithm: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="zstd">ZSTD - Best compression ratio (recommended)</option>
                        <option value="gzip">GZIP - Good compression, widely compatible</option>
                        <option value="lzo">LZO - Fastest, lower compression</option>
                        <option value="none">None - No compression</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Default Retention (Days)</label>
                      <input
                        type="number"
                        value={settingsForm.default_retention_days}
                        onChange={(e) => setSettingsForm({ ...settingsForm, default_retention_days: parseInt(e.target.value) || 30 })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                        max="365"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Concurrent Backups</label>
                      <input
                        type="number"
                        value={settingsForm.max_concurrent_backups}
                        onChange={(e) => setSettingsForm({ ...settingsForm, max_concurrent_backups: parseInt(e.target.value) || 2 })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                </div>

                {/* Enable Toggle */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="enabled"
                        type="checkbox"
                        checked={settingsForm.enabled}
                        onChange={(e) => setSettingsForm({ ...settingsForm, enabled: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-4">
                      <label htmlFor="enabled" className="text-lg font-semibold text-gray-900">Enable S3 Cloud Backups</label>
                      <p className="text-sm text-gray-600 mt-1">
                        When enabled, the backup scheduler will run and all backup operations will upload to your configured S3 bucket.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveSettings}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    💾 Save Settings
                  </button>
                  <button
                    onClick={handleTestConnection}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    🔗 Test Connection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Backup Modal */}
      {showBackupForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Create Manual Backup</h3>
              <p className="text-sm text-gray-500 mt-1">Create an on-demand backup of a VM to S3</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select VM *</label>
                <select
                  value={backupVpsId}
                  onChange={(e) => setBackupVpsId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Select a VM --</option>
                  {vpsList.map((vps) => (
                    <option key={vps.id} value={vps.id}>
                      {vps.name || vps.hostname} (VMID: {vps.proxmox_vmid}) - {vps.status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Backup Name (Optional)</label>
                <input
                  type="text"
                  value={backupName}
                  onChange={(e) => setBackupName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., pre-upgrade-backup"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                <textarea
                  value={backupDescription}
                  onChange={(e) => setBackupDescription(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Reason for this backup..."
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                onClick={() => { setShowBackupForm(false); setBackupVpsId(''); setBackupName(''); setBackupDescription(''); }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBackup}
                disabled={!backupVpsId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-medium"
              >
                Start Backup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Schedule Modal */}
      {showScheduleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-xl font-semibold text-gray-900">Create Backup Schedule</h3>
              <p className="text-sm text-gray-500 mt-1">Set up automated backups on a recurring schedule</p>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Name *</label>
                  <input
                    type="text"
                    value={scheduleForm.name}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Weekly Full Backup"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <input
                    type="text"
                    value={scheduleForm.description}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Backup all production VMs every Sunday"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                  <select
                    value={scheduleForm.frequency}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {scheduleForm.frequency === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Day of Week</label>
                    <select
                      value={scheduleForm.day_of_week}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
                        <option key={idx} value={idx}>{day}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time (UTC)</label>
                  <input
                    type="time"
                    value={scheduleForm.time_utc}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, time_utc: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Backup Mode</label>
                  <select
                    value={scheduleForm.backup_mode}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, backup_mode: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="snapshot">Snapshot (No downtime)</option>
                    <option value="suspend">Suspend (Brief pause)</option>
                    <option value="stop">Stop (Most consistent)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Compression</label>
                  <select
                    value={scheduleForm.compression}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, compression: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="zstd">ZSTD (Best)</option>
                    <option value="gzip">GZIP</option>
                    <option value="lzo">LZO (Fast)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Keep Last N Backups</label>
                  <input
                    type="number"
                    value={scheduleForm.retention_count}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, retention_count: parseInt(e.target.value) || 4 })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="52"
                  />
                  <p className="text-xs text-gray-500 mt-1">Older backups will be deleted automatically</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Retention Days</label>
                  <input
                    type="number"
                    value={scheduleForm.retention_days}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, retention_days: parseInt(e.target.value) || 30 })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={scheduleForm.enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 font-medium text-gray-900">Enable this schedule immediately</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                onClick={() => setShowScheduleForm(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSchedule}
                disabled={!scheduleForm.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 font-medium"
              >
                Create Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Details Modal */}
      {selectedBackup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Backup Details</h3>
                <p className="text-sm text-gray-500">{selectedBackup.name}</p>
              </div>
              <button onClick={() => setSelectedBackup(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <div className="p-6 space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${getStatusConfig(selectedBackup.status).bg}`}>
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{getStatusConfig(selectedBackup.status).icon}</span>
                  <div>
                    <p className={`font-semibold ${getStatusConfig(selectedBackup.status).text}`}>
                      {getStatusConfig(selectedBackup.status).label}
                    </p>
                    {selectedBackup.error_message && (
                      <p className="text-sm text-red-600 mt-1">{selectedBackup.error_message}</p>
                    )}
                  </div>
                </div>
                {selectedBackup.status === 'uploading' && (
                  <div className="mt-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Upload Progress</span>
                      <span>{selectedBackup.progress_percent}%</span>
                    </div>
                    <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${selectedBackup.progress_percent}%` }}></div>
                    </div>
                  </div>
                )}
              </div>

              {/* VM Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase font-medium">VM Name</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedBackup.vps_name || selectedBackup.vps_hostname}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase font-medium">Proxmox VMID</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedBackup.proxmox_vmid}</p>
                </div>
              </div>

              {/* Backup Info */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Backup Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">File Size</span>
                    <span className="font-medium">{formatFileSize(selectedBackup.file_size_bytes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Compression</span>
                    <span className="font-medium uppercase">{selectedBackup.compression}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Backup Mode</span>
                    <span className="font-medium capitalize">{selectedBackup.backup_mode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-medium">{formatDuration(selectedBackup.started_at, selectedBackup.completed_at)}</span>
                  </div>
                </div>
              </div>

              {/* S3 Location */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">S3 Storage Location</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bucket</span>
                    <span className="font-medium font-mono text-sm">{selectedBackup.s3_bucket}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Region</span>
                    <span className="font-medium">{selectedBackup.s3_region}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block mb-1">S3 Key</span>
                    <code className="text-xs bg-gray-200 px-2 py-1 rounded block overflow-x-auto">{selectedBackup.s3_key}</code>
                  </div>
                  <div>
                    <span className="text-gray-600 block mb-1">File Name</span>
                    <code className="text-xs bg-gray-200 px-2 py-1 rounded block overflow-x-auto">{selectedBackup.file_name}</code>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Timeline</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created</span>
                    <span className="font-medium">{formatDate(selectedBackup.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Started</span>
                    <span className="font-medium">{formatDate(selectedBackup.started_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed</span>
                    <span className="font-medium">{formatDate(selectedBackup.completed_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expires</span>
                    <span className="font-medium">{selectedBackup.expires_at ? formatDate(selectedBackup.expires_at) : 'Never'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Retention</span>
                    <span className="font-medium">{selectedBackup.retention_days} days</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                {selectedBackup.status === 'completed' && (
                  <button
                    onClick={() => { openRestoreModal(selectedBackup); setSelectedBackup(null); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Restore This Backup
                  </button>
                )}
                {!selectedBackup.is_protected && !['creating', 'uploading', 'restoring'].includes(selectedBackup.status) && (
                  <button
                    onClick={() => { handleDeleteBackup(selectedBackup.id, selectedBackup.name); }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  >
                    Delete Backup
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enterprise Restore Modal */}
      {showRestoreModal && restoreBackup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">🔄</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">Restore from Backup</h2>
                    <p className="text-blue-100 text-sm">Restore VM from S3 cloud backup</p>
                  </div>
                </div>
                {!restoreInProgress && (
                  <button
                    onClick={() => setShowRestoreModal(false)}
                    className="text-white hover:text-blue-200 text-2xl font-bold"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {/* Backup Info Card */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 mb-6 border border-blue-100">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">📦</span> Source Backup
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Backup Name</span>
                    <p className="font-medium text-gray-800">{restoreBackup.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">VM Name</span>
                    <p className="font-medium text-gray-800">{restoreBackup.vps_name || restoreBackup.vps_hostname}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Original VMID</span>
                    <p className="font-medium text-gray-800">{restoreBackup.proxmox_vmid}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Backup Size</span>
                    <p className="font-medium text-gray-800">
                      {restoreBackup.file_size_gb ? `${parseFloat(String(restoreBackup.file_size_gb)).toFixed(2)} GB` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Created</span>
                    <p className="font-medium text-gray-800">{new Date(restoreBackup.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">S3 Location</span>
                    <p className="font-medium text-gray-800 text-xs truncate" title={restoreBackup.s3_key}>
                      s3://{restoreBackup.s3_bucket}/...
                    </p>
                  </div>
                </div>
              </div>

              {!restoreInProgress ? (
                <>
                  {/* Restore Options */}
                  <div className="space-y-6">
                    {/* Restore Mode */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        🎯 Restore Destination
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          onClick={() => setRestoreOptions({ ...restoreOptions, restoreMode: 'same' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            restoreOptions.restoreMode === 'same'
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-center mb-1">
                            <span className="text-xl mr-2">♻️</span>
                            <span className="font-semibold text-sm">Same VM</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            Replace VM {restoreBackup.proxmox_vmid}
                          </p>
                        </button>
                        <button
                          onClick={() => setRestoreOptions({ ...restoreOptions, restoreMode: 'new' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            restoreOptions.restoreMode === 'new'
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-center mb-1">
                            <span className="text-xl mr-2">➕</span>
                            <span className="font-semibold text-sm">New VM</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            Different VMID, same host
                          </p>
                        </button>
                        <button
                          onClick={() => {
                            setRestoreOptions({ ...restoreOptions, restoreMode: 'cross-host' });
                          }}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            restoreOptions.restoreMode === 'cross-host'
                              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center mb-1">
                            <span className="text-xl mr-2">🌐</span>
                            <span className="font-semibold text-sm">Cross-Host</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            Restore to different server
                          </p>
                        </button>
                      </div>
                    </div>

                    {/* Target VMID (only for new mode) */}
                    {restoreOptions.restoreMode === 'new' && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          🔢 Target VMID
                        </label>
                        <input
                          type="number"
                          value={restoreOptions.targetVmid}
                          onChange={(e) => setRestoreOptions({ ...restoreOptions, targetVmid: e.target.value })}
                          placeholder="Enter new VMID (e.g., 200)"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          min="100"
                          max="999999"
                        />
                        <p className="mt-2 text-xs text-yellow-700">
                          Enter a unique VMID that doesn't exist on the Proxmox host
                        </p>
                      </div>
                    )}

                    {/* Cross-Host Restore Configuration */}
                    {restoreOptions.restoreMode === 'cross-host' && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                        <div className="flex items-center mb-2">
                          <span className="text-xl mr-2">🌐</span>
                          <h4 className="font-semibold text-purple-800">Cross-Host Restore Configuration</h4>
                        </div>

                        {/* Target Host Selection */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            🖥️ Target Proxmox Host
                          </label>
                          <select
                            value={restoreOptions.targetHostId}
                            onChange={(e) => {
                              setRestoreOptions({ ...restoreOptions, targetHostId: e.target.value, specificIpAddress: '' });
                              fetchAvailableIps(e.target.value);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            disabled={loadingHosts}
                          >
                            <option value="">Select a Proxmox host...</option>
                            {proxmoxHosts.map((host: any) => (
                              <option key={host.id} value={host.id}>
                                {host.name} ({host.hostname}) - {host.is_active ? 'Active' : 'Inactive'}
                              </option>
                            ))}
                          </select>
                          {loadingHosts && (
                            <p className="mt-1 text-xs text-purple-600">Loading hosts...</p>
                          )}
                        </div>

                        {/* IP Address Selection */}
                        {restoreOptions.targetHostId && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              🌐 IP Address Assignment
                            </label>
                            <div className="space-y-2">
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  checked={restoreOptions.allocateNewIp}
                                  onChange={() => setRestoreOptions({ ...restoreOptions, allocateNewIp: true, specificIpAddress: '' })}
                                  className="mr-2"
                                />
                                <span className="text-sm">Auto-allocate from pool</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  checked={!restoreOptions.allocateNewIp}
                                  onChange={() => setRestoreOptions({ ...restoreOptions, allocateNewIp: false })}
                                  className="mr-2"
                                />
                                <span className="text-sm">Select specific IP</span>
                              </label>
                            </div>

                            {restoreOptions.allocateNewIp ? (
                              <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                                {loadingIps ? (
                                  <span>Loading available IPs...</span>
                                ) : availableIps.length > 0 ? (
                                  <span>✓ {availableIps.length} IP(s) available. System will auto-assign.</span>
                                ) : (
                                  <span className="text-red-600">⚠ No available IPs on this host</span>
                                )}
                              </div>
                            ) : (
                              <select
                                value={restoreOptions.specificIpAddress}
                                onChange={(e) => setRestoreOptions({ ...restoreOptions, specificIpAddress: e.target.value })}
                                className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                disabled={loadingIps}
                              >
                                <option value="">Select an available IP...</option>
                                {availableIps.map((ip: any) => (
                                  <option key={ip.id} value={ip.ip_address}>
                                    {ip.ip_address} / {ip.netmask} (Gateway: {ip.gateway})
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}

                        {/* New VPS Record Options */}
                        <div className="border-t border-purple-200 pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <label className="font-semibold text-gray-700 text-sm">📋 Create New VPS Record</label>
                              <p className="text-xs text-gray-500">Create a new VPS entry in the database</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={restoreOptions.createNewVpsRecord}
                                onChange={(e) => setRestoreOptions({ ...restoreOptions, createNewVpsRecord: e.target.checked })}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                            </label>
                          </div>

                          {restoreOptions.createNewVpsRecord && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                VPS Name
                              </label>
                              <input
                                type="text"
                                value={restoreOptions.newVpsName}
                                onChange={(e) => setRestoreOptions({ ...restoreOptions, newVpsName: e.target.value })}
                                placeholder="e.g., my-server-restored"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Target Storage */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        💾 Target Storage
                      </label>
                      <select
                        value={restoreOptions.targetStorage}
                        onChange={(e) => setRestoreOptions({ ...restoreOptions, targetStorage: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="local-zfs">local-zfs (Recommended)</option>
                        <option value="local">local</option>
                        <option value="local-lvm">local-lvm</option>
                      </select>
                    </div>

                    {/* Auto-start option */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="font-semibold text-gray-700">⚡ Auto-start VM after restore</label>
                        <p className="text-sm text-gray-500">Automatically start the VM when restore completes</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={restoreOptions.startAfterRestore}
                          onChange={(e) => setRestoreOptions({ ...restoreOptions, startAfterRestore: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Warning */}
                    <div className={`border rounded-lg p-4 ${
                      restoreOptions.restoreMode === 'cross-host'
                        ? 'bg-purple-50 border-purple-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start">
                        <span className="text-2xl mr-3">
                          {restoreOptions.restoreMode === 'cross-host' ? '🌐' : '⚠️'}
                        </span>
                        <div>
                          <h4 className={`font-semibold ${
                            restoreOptions.restoreMode === 'cross-host' ? 'text-purple-800' : 'text-red-800'
                          }`}>
                            {restoreOptions.restoreMode === 'cross-host' ? 'Cross-Host Restore' : 'Warning'}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            restoreOptions.restoreMode === 'cross-host' ? 'text-purple-700' : 'text-red-700'
                          }`}>
                            {restoreOptions.restoreMode === 'same'
                              ? `This will completely replace VM ${restoreBackup.proxmox_vmid} with the backup contents. All current data on this VM will be lost. The VM will be stopped during the restore process.`
                              : restoreOptions.restoreMode === 'new'
                              ? `This will create a new VM with VMID ${restoreOptions.targetVmid || '[enter VMID]'} from the backup. Make sure this VMID doesn't already exist.`
                              : `This will restore the backup to a different Proxmox host. A new VMID will be automatically assigned. ${
                                  restoreOptions.allocateNewIp
                                    ? 'A new IP address will be allocated from the target host\'s pool.'
                                    : restoreOptions.specificIpAddress
                                    ? `The VM will use IP ${restoreOptions.specificIpAddress}.`
                                    : 'Please select an IP address.'
                                } ${
                                  restoreOptions.createNewVpsRecord
                                    ? 'A new VPS record will be created in the database.'
                                    : ''
                                }`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                    <button
                      onClick={() => setShowRestoreModal(false)}
                      className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={executeRestore}
                      className={`px-6 py-2.5 text-white rounded-lg font-medium flex items-center ${
                        restoreOptions.restoreMode === 'cross-host'
                          ? 'bg-purple-600 hover:bg-purple-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      <span className="mr-2">{restoreOptions.restoreMode === 'cross-host' ? '🌐' : '🔄'}</span>
                      {restoreOptions.restoreMode === 'cross-host' ? 'Start Cross-Host Restore' : 'Start Restore'}
                    </button>
                  </div>
                </>
              ) : (
                /* Progress View */
                <div className="text-center py-8">
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 mb-4">
                      {restoreProgress >= 100 ? (
                        <span className="text-4xl">✅</span>
                      ) : (
                        <svg className="w-10 h-10 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {restoreProgress >= 100 ? 'Restore Complete!' : 'Restoring VM...'}
                    </h3>
                    <p className="text-gray-600">{restoreStatus}</p>
                  </div>

                  {/* Progress Bar */}
                  <div className="max-w-md mx-auto mb-6">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Progress</span>
                      <span>{Math.round(restoreProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-4 rounded-full transition-all duration-500 ${
                          restoreProgress >= 100 ? 'bg-green-500' : 'bg-blue-600'
                        }`}
                        style={{ width: `${restoreProgress}%` }}
                      />
                    </div>
                  </div>

                  {/* Restore Steps */}
                  <div className="max-w-md mx-auto text-left space-y-3">
                    <div className={`flex items-center p-3 rounded-lg ${restoreProgress >= 5 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                      <span className="mr-3">{restoreProgress >= 5 ? '✅' : '⏳'}</span>
                      <span>Initiating restore process</span>
                    </div>
                    <div className={`flex items-center p-3 rounded-lg ${restoreProgress >= 20 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                      <span className="mr-3">{restoreProgress >= 20 ? '✅' : restoreProgress >= 10 ? '🔄' : '⏳'}</span>
                      <span>Downloading backup from S3</span>
                    </div>
                    <div className={`flex items-center p-3 rounded-lg ${restoreProgress >= 50 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                      <span className="mr-3">{restoreProgress >= 50 ? '✅' : restoreProgress >= 20 ? '🔄' : '⏳'}</span>
                      <span>Stopping target VM</span>
                    </div>
                    <div className={`flex items-center p-3 rounded-lg ${restoreProgress >= 90 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                      <span className="mr-3">{restoreProgress >= 90 ? '✅' : restoreProgress >= 50 ? '🔄' : '⏳'}</span>
                      <span>Restoring VM data</span>
                    </div>
                    <div className={`flex items-center p-3 rounded-lg ${restoreProgress >= 100 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                      <span className="mr-3">{restoreProgress >= 100 ? '✅' : '⏳'}</span>
                      <span>{restoreOptions.startAfterRestore ? 'Starting VM' : 'Completing restore'}</span>
                    </div>
                  </div>

                  {restoreProgress >= 100 && (
                    <div className="mt-6">
                      <p className="text-green-600 font-medium">
                        VM has been successfully restored from backup!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default S3BackupManagement;
